import { useMemo, useRef, useEffect, useState } from "react";
import DottedMap from "dotted-map";
import { motion, useReducedMotion } from "framer-motion";

// Premium accent — institutional pastel green
const ACCENT = "#D9F99D";
const ACCENT_DIM = "#BEF264";

// Key global financial hubs the brand operates from.
const HUBS = [
  { name: "New York",  lat: 40.7128,  lng: -74.006  },
  { name: "London",    lat: 51.5074,  lng:  -0.1278 },
  { name: "Zurich",    lat: 47.3769,  lng:   8.5417 },
  { name: "Frankfurt", lat: 50.1109,  lng:   8.6821 },
  { name: "Dubai",     lat: 25.2048,  lng:  55.2708 },
  { name: "Singapore", lat:  1.3521,  lng: 103.8198 },
  { name: "Hong Kong", lat: 22.3193,  lng: 114.1694 },
  { name: "Tokyo",     lat: 35.6762,  lng: 139.6503 },
  { name: "Sydney",    lat:-33.8688,  lng: 151.2093 },
  { name: "Ahmedabad", lat: 23.0225,  lng:  72.5714, highlight: true },
];

// Connection graph — a curated subset for a clean editorial look
const LINKS = [
  ["London", "New York"],
  ["London", "Frankfurt"],
  ["London", "Dubai"],
  ["Dubai", "Singapore"],
  ["Dubai", "Ahmedabad"],
  ["Singapore", "Hong Kong"],
  ["Hong Kong", "Tokyo"],
  ["Tokyo", "Sydney"],
  ["New York", "Zurich"],
  ["Frankfurt", "Zurich"],
  ["Ahmedabad", "Singapore"],
];

// Equirectangular projection matching dotted-map default (w:119, h:60)
const MAP_W = 119;
const MAP_H = 60;
const project = (lat, lng) => ({
  x: ((lng + 180) / 360) * MAP_W,
  y: ((90 - lat) / 180) * MAP_H,
});

// Build a smooth curved path between two projected points
const curvedPath = (a, b) => {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  // Lift the curve upward proportional to distance (editorial arc)
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const cy = my - Math.min(dist * 0.28, 14);
  return `M ${a.x},${a.y} Q ${mx},${cy} ${b.x},${b.y}`;
};

export default function WorldMap() {
  const reduced = useReducedMotion();
  const wrapRef = useRef(null);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });

  // Generate the dotted-map SVG once (heavy) — memoised across renders
  const dotsDataUrl = useMemo(() => {
    const map = new DottedMap({ height: 60, grid: "diagonal" });
    const svg = map.getSVG({
      radius: 0.32,
      color: "rgba(255,255,255,0.38)",
      shape: "circle",
      backgroundColor: "transparent",
    });
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }, []);

  // Projected hub positions
  const hubs = useMemo(
    () => HUBS.map((h) => ({ ...h, ...project(h.lat, h.lng) })),
    [],
  );
  const hubByName = useMemo(
    () => Object.fromEntries(hubs.map((h) => [h.name, h])),
    [hubs],
  );

  // Subtle mouse parallax
  useEffect(() => {
    if (reduced) return;
    const el = wrapRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const x = ((e.clientX - cx) / r.width) * 10;
      const y = ((e.clientY - cy) / r.height) * 8;
      setParallax({ x, y });
    };
    const onLeave = () => setParallax({ x: 0, y: 0 });
    window.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [reduced]);

  return (
    <div
      ref={wrapRef}
      data-testid="hero-world-map"
      className="relative w-full aspect-[119/60] select-none"
      aria-label="Global executive search network — major financial hubs"
      role="img"
    >
      {/* Glassmorphism backdrop */}
      <div
        aria-hidden
        className="absolute -inset-8 sm:-inset-10 rounded-[32px] pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 70% at 50% 50%, rgba(217,249,157,0.07), rgba(217,249,157,0) 72%)",
          filter: "blur(22px)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 rounded-3xl pointer-events-none"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.015) 40%, rgba(255,255,255,0.03))",
          boxShadow:
            "inset 0 0 0 1px rgba(217,249,157,0.10), inset 0 0 80px rgba(217,249,157,0.05)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
      />

      {/* Motion wrapper — very subtle parallax + float */}
      <motion.div
        className="absolute inset-0"
        animate={{ x: parallax.x, y: parallax.y }}
        transition={{ type: "spring", stiffness: 40, damping: 18, mass: 0.9 }}
      >
        {/* Dotted world map */}
        <img
          src={dotsDataUrl}
          alt=""
          draggable={false}
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          style={{
            opacity: 0.85,
            filter:
              "drop-shadow(0 0 8px rgba(217,249,157,0.06))",
          }}
        />

        {/* Hubs + arcs */}
        <svg
          viewBox={`0 0 ${MAP_W} ${MAP_H}`}
          className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <defs>
            <radialGradient id="zx-hub-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={ACCENT} stopOpacity="0.75" />
              <stop offset="60%" stopColor={ACCENT} stopOpacity="0.12" />
              <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
            </radialGradient>
            <linearGradient id="zx-arc" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%"   stopColor={ACCENT} stopOpacity="0" />
              <stop offset="50%"  stopColor={ACCENT} stopOpacity="0.55" />
              <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Connection arcs */}
          {LINKS.map(([a, b], i) => {
            const p1 = hubByName[a];
            const p2 = hubByName[b];
            if (!p1 || !p2) return null;
            const d = curvedPath(p1, p2);
            return (
              <g key={`${a}-${b}`}>
                <path
                  d={d}
                  fill="none"
                  stroke="url(#zx-arc)"
                  strokeWidth="0.18"
                  strokeLinecap="round"
                  opacity="0.85"
                />
                {!reduced && (
                  <circle r="0.28" fill={ACCENT} opacity="0.9">
                    <animateMotion
                      dur={`${5 + (i % 4) * 1.2}s`}
                      begin={`${i * 0.35}s`}
                      repeatCount="indefinite"
                      path={d}
                    />
                  </circle>
                )}
              </g>
            );
          })}

          {/* Hubs */}
          {hubs.map((h) => {
            const r = h.highlight ? 0.85 : 0.55;
            return (
              <g key={h.name}>
                {/* Soft glow halo */}
                <circle
                  cx={h.x}
                  cy={h.y}
                  r={r * 4}
                  fill="url(#zx-hub-glow)"
                />
                {/* Pulsing ring */}
                {!reduced && (
                  <circle
                    cx={h.x}
                    cy={h.y}
                    r={r}
                    fill="none"
                    stroke={ACCENT}
                    strokeWidth="0.15"
                    opacity="0.6"
                  >
                    <animate
                      attributeName="r"
                      from={r}
                      to={r * 3.5}
                      dur={h.highlight ? "2.2s" : "2.8s"}
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      from="0.55"
                      to="0"
                      dur={h.highlight ? "2.2s" : "2.8s"}
                      repeatCount="indefinite"
                    />
                  </circle>
                )}
                {/* Core node */}
                <circle
                  cx={h.x}
                  cy={h.y}
                  r={r}
                  fill={h.highlight ? ACCENT : ACCENT_DIM}
                  stroke={ACCENT}
                  strokeWidth="0.12"
                  style={{
                    filter: `drop-shadow(0 0 ${h.highlight ? 1.2 : 0.6}px ${ACCENT})`,
                  }}
                />
              </g>
            );
          })}
        </svg>
      </motion.div>

      {/* Corner meta labels */}
      <div className="pointer-events-none absolute top-3 left-4 text-[10px] font-mono uppercase tracking-[0.28em] text-[#D9F99D]/90">
        ZX.NET · Live
      </div>
      <div className="pointer-events-none absolute top-3 right-4 text-[10px] font-mono uppercase tracking-[0.28em] text-neutral-400">
        10 Hubs
      </div>
      <div className="pointer-events-none absolute bottom-3 left-4 text-[10px] font-mono uppercase tracking-[0.28em] text-neutral-400">
        Global Response
      </div>
      <div className="pointer-events-none absolute bottom-3 right-4 text-[10px] font-mono uppercase tracking-[0.28em] text-neutral-400">
        Lat 23.02°N · Lng 72.57°E
      </div>
    </div>
  );
}
